// Parallax themed backgrounds for the three levels, drawn in screen space
// using the camera x for parallax offsets.
import { GAME } from "./config";

function rect(ctx, x, y, w, h, color) {
  ctx.fillStyle = color;
  ctx.fillRect(Math.round(x), Math.round(y), Math.round(w), Math.round(h));
}

function sky(ctx, top, bottom) {
  const g = ctx.createLinearGradient(0, 0, 0, GAME.HEIGHT);
  g.addColorStop(0, top);
  g.addColorStop(1, bottom);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, GAME.WIDTH, GAME.HEIGHT);
}

// repeat helper: draws a unit every `step` across the screen with parallax
function repeatX(camX, parallax, step, cb) {
  const off = -((camX * parallax) % step);
  for (let x = off - step; x < GAME.WIDTH + step; x += step) {
    cb(x);
  }
}

export function drawBackground(ctx, level, camX, frame) {
  if (level.theme === "managua") drawManagua(ctx, level, camX, frame);
  else if (level.theme === "granada") drawGranada(ctx, level, camX, frame);
  else drawSanJuan(ctx, level, camX, frame);
}

function drawManagua(ctx, level, camX, frame) {
  sky(ctx, level.sky[0], level.sky[1]);
  // clouds (far)
  repeatX(camX, 0.15, 360, (x) => {
    rect(ctx, x + 40, 70, 90, 24, "rgba(255,255,255,0.9)");
    rect(ctx, x + 70, 56, 60, 22, "rgba(255,255,255,0.9)");
    rect(ctx, x + 200, 110, 70, 18, "rgba(255,255,255,0.8)");
  });
  // control towers + terminal (mid)
  repeatX(camX, 0.4, 520, (x) => {
    rect(ctx, x + 60, 300, 50, 90, "#d7dbe0"); // tower base
    rect(ctx, x + 50, 270, 70, 34, "#9fb3c7"); // tower top (glass)
    rect(ctx, x + 50, 270, 70, 8, "#6f8aa3");
    rect(ctx, x + 78, 200, 6, 70, "#bcc4cc"); // antenna
    // terminal building
    rect(ctx, x + 180, 330, 240, 60, "#e8eef3");
    rect(ctx, x + 180, 330, 240, 12, "#c0ccd6");
    for (let i = 0; i < 10; i++) rect(ctx, x + 192 + i * 22, 348, 12, 22, "#7fa8cf");
    // parked plane
    rect(ctx, x + 300, 300, 120, 16, "#ffffff");
    rect(ctx, x + 300, 300, 120, 5, "#cfd6dc");
    rect(ctx, x + 296, 294, 14, 12, "#ff5722"); // nose
    rect(ctx, x + 348, 282, 8, 20, "#ffffff"); // tail
    rect(ctx, x + 330, 312, 40, 8, "#9fb3c7"); // wing
  });
}

function drawGranada(ctx, level, camX, frame) {
  sky(ctx, level.sky[0], level.sky[1]);
  // Mombacho volcano (far)
  repeatX(camX, 0.12, 900, (x) => {
    ctx.fillStyle = "#5b7d5b";
    ctx.beginPath();
    ctx.moveTo(x + 120, 330);
    ctx.lineTo(x + 320, 120);
    ctx.lineTo(x + 520, 330);
    ctx.closePath();
    ctx.fill();
    rect(ctx, x + 270, 120, 100, 30, "#3f5e3f");
  });
  // colourful colonial facades (mid)
  const cols = ["#f2c14e", "#e07a5f", "#3d9bd6", "#88b04b", "#d65db1", "#f08a5d"];
  repeatX(camX, 0.45, 130, (x) => {
    const i = Math.abs(Math.floor((x + camX * 0.45) / 130)) % cols.length;
    rect(ctx, x, 250, 128, 140, cols[i]);
    rect(ctx, x, 250, 128, 10, "rgba(0,0,0,0.15)");
    rect(ctx, x + 20, 280, 30, 44, "#5a3b2a"); // door/window
    rect(ctx, x + 78, 280, 30, 44, "#5a3b2a");
    rect(ctx, x, 244, 128, 8, "#7a4a2a"); // roof tile line
  });
  // Granada cathedral towers
  repeatX(camX, 0.45, 780, (x) => {
    rect(ctx, x + 320, 180, 80, 210, "#f2d49b");
    rect(ctx, x + 336, 150, 48, 34, "#e6b85c"); // dome
    rect(ctx, x + 354, 130, 12, 22, "#e6b85c");
    rect(ctx, x + 340, 230, 16, 40, "#7a4a2a"); // door
    rect(ctx, x + 372, 230, 16, 40, "#7a4a2a");
  });
}

function drawSanJuan(ctx, level, camX, frame) {
  sky(ctx, level.sky[0], level.sky[1]);
  // big sun
  rect(ctx, 700, 80, 90, 90, "#ffe066");
  rect(ctx, 690, 100, 110, 50, "#ffd24a");
  // hills with Christ statue (far)
  repeatX(camX, 0.12, 1000, (x) => {
    ctx.fillStyle = "#7a5a8a";
    ctx.beginPath();
    ctx.moveTo(x + 60, 330);
    ctx.lineTo(x + 240, 170);
    ctx.lineTo(x + 420, 330);
    ctx.closePath();
    ctx.fill();
    // Cristo de la Misericordia
    rect(ctx, x + 236, 130, 8, 44, "#f0eada");
    rect(ctx, x + 218, 146, 44, 8, "#f0eada"); // arms
  });
  // ocean (mid)
  rect(ctx, 0, 330, GAME.WIDTH, 60, "#1f9fd6");
  repeatX(camX, 0.5, 80, (x) => {
    rect(ctx, x, 336 + (Math.sin((x + frame) * 0.05) * 3), 40, 5, "#bfeaff");
  });
  // palm trees (near-ish)
  repeatX(camX, 0.7, 420, (x) => {
    rect(ctx, x + 80, 300, 12, 80, "#7a4a2a");
    rect(ctx, x + 58, 286, 40, 12, "#2f9e44");
    rect(ctx, x + 86, 274, 40, 12, "#37b24d");
    rect(ctx, x + 70, 296, 50, 8, "#2b8a3e");
  });
}
